import {
  ROOM_PATH_SEGMENTS,
  ROOM_TAB_SEGMENTS,
  getRoomDetailsPath,
  getRoomsAcceptPath,
  getRoomsBasePath,
  getRoomsCreatePath,
  getRoomTabRoute,
} from '../../service/constants';

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const encodeSegment = (value) => encodeURIComponent(toStringOrNull(value) || '');

const roomBase = (roomId) => `${getRoomDetailsPath(encodeSegment(roomId))}`;

const allRoomPathSegments = Object.values(ROOM_PATH_SEGMENTS);
const allRoomTabSegments = Object.values(ROOM_TAB_SEGMENTS);
const allRoomBaseSegments = new Set(allRoomPathSegments.map((segments) => segments.base));
const allRoomCreateSegments = new Set(allRoomPathSegments.map((segments) => segments.create));
const allRoomAcceptSegments = new Set(allRoomPathSegments.map((segments) => segments.accept));
const allRoomChatSegments = new Set(allRoomTabSegments.map((segments) => segments.members_chat));

const safeDecodeSegment = (value) => {
  try {
    return decodeURIComponent(String(value || ''));
  } catch {
    return String(value || '');
  }
};

const appendLocationSuffix = (pathname, search = '', hash = '') =>
  `${pathname}${String(search || '')}${String(hash || '')}`;

export const buildRoomChatPath = (roomId) =>
  `${roomBase(roomId)}/${getRoomTabRoute('members_chat')}`;

export const buildRoomInfoPath = (roomId, section = 'overview') => {
  const requestedSection = toStringOrNull(section) || 'overview';
  const normalizedSection =
    requestedSection === 'members'
      ? 'people'
      : ['links', 'docs'].includes(requestedSection)
        ? 'media'
        : requestedSection === 'permissions'
          ? 'settings'
          : requestedSection;
  return normalizedSection === 'overview'
    ? `${roomBase(roomId)}/info`
    : `${roomBase(roomId)}/info/${encodeSegment(normalizedSection)}`;
};

export const resolveRoomInfoRoute = (pathname = '', search = '', expectedRoomId = null) => {
  const normalizedPath = String(pathname || '').replace(/\/+$/, '');
  const roomsBasePath = getRoomsBasePath().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = normalizedPath.match(new RegExp(`^${roomsBasePath}/([^/]+)/info(?:/([^/]+))?$`));
  if (!match) return null;

  const roomId = decodeURIComponent(match[1]);
  if (expectedRoomId != null && roomId !== String(expectedRoomId)) return null;
  const routeSection = toStringOrNull(match[2]) || 'overview';
  const mediaTab = new URLSearchParams(String(search || '')).get('tab');
  return {
    type: 'info',
    roomId,
    section:
      routeSection === 'people'
        ? 'members'
        : routeSection === 'media' && ['links', 'docs'].includes(mediaTab)
          ? mediaTab
          : routeSection,
  };
};

export const buildRoomMediaPath = (roomId, messageId, index = null) => {
  const basePath = `${buildRoomChatPath(roomId)}/media/${encodeSegment(messageId)}`;
  return index == null ? basePath : `${basePath}/${Math.max(0, Math.trunc(Number(index) || 0))}`;
};

export const buildRoomAttachmentPath = (roomId, draftId) =>
  `${buildRoomChatPath(roomId)}/attachments/${encodeSegment(draftId)}`;

export const buildRoomImageEditorPath = (roomId, draftId, { tool = null, layerId = null } = {}) => {
  const editorPath = `${buildRoomAttachmentPath(roomId, draftId)}/edit`;
  const normalizedTool = toStringOrNull(tool);
  const normalizedLayerId = toStringOrNull(layerId);
  return `${editorPath}${normalizedTool ? `/${encodeSegment(normalizedTool)}` : ''}${
    normalizedLayerId ? `/${encodeSegment(normalizedLayerId)}` : ''
  }`;
};

export const buildRoomForwardPath = (roomId, messageId, attachmentIndex = null) => {
  const query =
    attachmentIndex == null
      ? ''
      : `?attachment=${Math.max(0, Math.trunc(Number(attachmentIndex) || 0))}`;
  return `${buildRoomChatPath(roomId)}/forward/${encodeSegment(messageId)}${query}`;
};

export const buildRoomCallPath = (roomId, callId) =>
  `${roomBase(roomId)}/calls/${encodeSegment(callId)}`;

export const isRoomOwnedPath = (pathname = '', roomId = null) => {
  const normalizedPath = String(pathname || '');
  const expectedBase = roomId == null ? getRoomsBasePath() : roomBase(roomId);
  return normalizedPath === expectedBase || normalizedPath.startsWith(`${expectedBase}/`);
};

export const getRoomBasePathAliases = () =>
  Array.from(allRoomBaseSegments, (segment) => `/${segment}`);

export const resolveRoomLegacyTabTarget = (tab = '') => {
  const normalizedTab = String(tab || '').toLowerCase();
  for (const segments of allRoomTabSegments) {
    if (normalizedTab === String(segments.about || '').toLowerCase()) return 'info';
    if (normalizedTab === String(segments.members || '').toLowerCase()) return 'info/people';
    if (normalizedTab === String(segments.files || '').toLowerCase()) return 'info/media';
    if (normalizedTab === String(segments.requests || '').toLowerCase()) return 'info/requests';
    if (normalizedTab === String(segments.settings || '').toLowerCase()) return 'info/settings';
    if (
      normalizedTab === String(segments.legacy_discussions || '').toLowerCase() ||
      normalizedTab === String(segments.members_chat || '').toLowerCase()
    ) {
      return getRoomTabRoute('members_chat');
    }
  }
  return null;
};

export const buildCanonicalRoomAliasPath = ({ pathname = '', search = '', hash = '' } = {}) => {
  const pathSegments = String(pathname || '')
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean);
  const sourceBase = pathSegments[0] || '';
  if (!allRoomBaseSegments.has(sourceBase)) return null;

  const originalLocation = appendLocationSuffix(`/${pathSegments.join('/')}`, search, hash);
  const canonicalize = (path) => {
    const canonicalLocation = appendLocationSuffix(path, search, hash);
    return canonicalLocation === originalLocation ? null : canonicalLocation;
  };
  const first = pathSegments[1] || '';
  if (!first) return canonicalize(getRoomsBasePath());
  if (allRoomCreateSegments.has(first)) return canonicalize(getRoomsCreatePath());
  if (allRoomAcceptSegments.has(first)) return canonicalize(getRoomsAcceptPath());

  const roomId = safeDecodeSegment(first);
  const roomDetailPath = roomBase(roomId);
  const routeSegment = pathSegments[2] || '';
  if (!routeSegment) return canonicalize(roomDetailPath);

  if (routeSegment === 'info') {
    return canonicalize(
      buildRoomInfoPath(roomId, safeDecodeSegment(pathSegments[3] || 'overview')),
    );
  }
  if (routeSegment === 'calls' && pathSegments[3]) {
    return canonicalize(buildRoomCallPath(roomId, safeDecodeSegment(pathSegments[3])));
  }
  if (allRoomChatSegments.has(routeSegment)) {
    const trailingPath = pathSegments.slice(3).join('/');
    const chatPath = buildRoomChatPath(roomId);
    return canonicalize(trailingPath ? `${chatPath}/${trailingPath}` : chatPath);
  }

  const legacyTarget = resolveRoomLegacyTabTarget(routeSegment);
  if (!legacyTarget) return null;
  return canonicalize(`${roomDetailPath}/${legacyTarget}`);
};

export const resolveRoomChatRoute = (pathname = '', search = '', expectedRoomId = null) => {
  const normalizedPath = String(pathname || '').replace(/\/+$/, '');
  const roomsBasePath = getRoomsBasePath().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const membersChatSegment = getRoomTabRoute('members_chat').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = normalizedPath.match(
    new RegExp(`^${roomsBasePath}/([^/]+)/${membersChatSegment}(?:/(.*))?$`),
  );
  if (!match) return null;

  const roomId = decodeURIComponent(match[1]);
  if (expectedRoomId != null && roomId !== String(expectedRoomId)) return null;
  const suffix = String(match[2] || '');
  if (!suffix) return { type: 'chat', roomId };

  let routeMatch = suffix.match(/^attachments\/([^/]+)(?:\/edit(?:\/([^/]+)(?:\/([^/]+))?)?)?$/);
  if (routeMatch) {
    const tool = toStringOrNull(routeMatch[2]);
    if (tool && !['crop', 'text', 'emoji', 'draw'].includes(tool)) return null;
    return {
      type: 'attachment',
      roomId,
      draftId: decodeURIComponent(routeMatch[1]),
      screen: suffix.includes('/edit') ? tool || 'edit' : 'composer',
      layerId: routeMatch[3] ? decodeURIComponent(routeMatch[3]) : null,
    };
  }

  routeMatch = suffix.match(/^media\/([^/]+)(?:\/(\d+))?$/);
  if (routeMatch) {
    return {
      type: 'media',
      roomId,
      messageId: decodeURIComponent(routeMatch[1]),
      index: Math.max(0, Number(routeMatch[2]) || 0),
    };
  }

  routeMatch = suffix.match(/^forward\/([^/]+)$/);
  if (routeMatch) {
    const rawAttachmentIndex = new URLSearchParams(String(search || '')).get('attachment');
    const parsedAttachmentIndex = rawAttachmentIndex == null ? null : Number(rawAttachmentIndex);
    return {
      type: 'forward',
      roomId,
      messageId: decodeURIComponent(routeMatch[1]),
      attachmentIndex:
        parsedAttachmentIndex != null && Number.isFinite(parsedAttachmentIndex)
          ? Math.max(0, Math.trunc(parsedAttachmentIndex))
          : null,
    };
  }

  return null;
};

export const resolveRoomSurfaceRoute = (pathname = '', search = '', expectedRoomId = null) =>
  resolveRoomChatRoute(pathname, search, expectedRoomId) ||
  resolveRoomInfoRoute(pathname, search, expectedRoomId);

export const createRoomMessengerRouteAdapter = (roomId) => ({
  buildAttachmentComposerPath: (draftId) => buildRoomAttachmentPath(roomId, draftId),
  buildCallPath: (callId) => buildRoomCallPath(roomId, callId),
  buildChatPath: () => buildRoomChatPath(roomId),
  buildForwardPath: (messageId, attachmentIndex = null) =>
    buildRoomForwardPath(roomId, messageId, attachmentIndex),
  buildImageEditorPath: (draftId, tool = null, layerId = null) =>
    buildRoomImageEditorPath(roomId, draftId, { tool, layerId }),
  buildInfoPath: (section = 'overview') => {
    const basePath = buildRoomInfoPath(roomId, section);
    return ['links', 'docs'].includes(section) ? `${basePath}?tab=${section}` : basePath;
  },
  buildMediaPath: (messageId, index = null) => buildRoomMediaPath(roomId, messageId, index),
  isOwnedPath: (pathname) => isRoomOwnedPath(pathname, roomId),
});
